import { useUnsafeCallback, useUnsafeEffect } from '@factorialco/deprecated-design-system'
import { FinancialWorkspacePayrollPoliciesDocument } from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import MultipleChoice from 'modules/core/components/Filter/filters/MultipleChoice'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useFilterProvider } from 'modules/finance/components/FinancialWorkspace/Providers/FiltersProvider'
import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'

interface PayrollPolicyFilterProps {
  defaultOpen?: boolean
  defaultPolicyIds?: string[]
}

export const PayrollPolicyFilter = ({
  defaultOpen,
  defaultPolicyIds,
}: PayrollPolicyFilterProps) => {
  const { saveFilters } = useFilterProvider()
  const {
    dispatch,
    state: { policyIds },
  } = useFinancialWorkspace()

  const { data } = useQuery(FinancialWorkspacePayrollPoliciesDocument)

  const policies = data?.payroll.policiesConnection.nodes || []

  const mappedPolicies = policies.map((m) => ({
    value: m.id,
    label: m.name ?? '',
  }))

  const options = useUnsafeMemo(
    () => orderBy(mappedPolicies, (policy) => policy.label, ['asc']),
    [mappedPolicies]
  )

  const onSelectPolicyIds = useUnsafeCallback(
    (value) => {
      dispatch({ type: 'SET_POLICY_IDS', payload: value })
      saveFilters('policyIds', value)
    },
    [dispatch, saveFilters]
  )

  // set default value as current value if there is no current value
  useUnsafeEffect(() => {
    if (defaultPolicyIds?.length && !policyIds?.length && options?.length) {
      onSelectPolicyIds(
        defaultPolicyIds.filter((id) => options.find((option) => option.value === id))
      )
    }
  }, [options, onSelectPolicyIds, defaultPolicyIds, policyIds])

  return (
    <MultipleChoice
      options={options}
      value={policyIds}
      defaultOpen={defaultOpen}
      onValueChange={onSelectPolicyIds}
      composeActiveLabel={(selected) => `(${selected.length})`}
      label={i18n.t('finance.financial_workspace.payroll.filters.policies')}
    />
  )
}
